/** @jsxImportSource @emotion/react */
import { Form } from 'react-final-form'

import { FormApi } from 'final-form'
import { FormLabel } from '@mui/material'

import { InputField } from 'components/Input'
import { EditorField } from 'components/Editor'
import { validateRequiredHtml } from 'utils/validation'
import { EmailTemplateTypeEnum } from '__generatedTypes__/globalTypes'
import Loader from 'components/Loader'
import { stripHTML } from 'utils/stripHTML'
import { toast } from 'utils/toast'
import { FooterButtons } from '../FooterButtons'
import { CheckboxWithTitle } from '../CheckboxWithTitle'
import * as commonClasses from '../commonStyles'
import { useEmailTemplate } from '../hooks'
import { validateEmailSubject } from '../consts'
import { CheckboxField } from '../../../components/Checkbox'
import * as classes from './styles'

type FormValues = {
  subject: string
  content: string
  disableForAdmin: boolean
  disableForMember: boolean
  daysPriorExpiration2: boolean
  daysPriorExpiration7: boolean
}

export type CommonEmailProps = {
  type: EmailTemplateTypeEnum
  platformId?: string
  withDisableForMember?: boolean
  withDisableForAdmin?: boolean
  withDaysPriorExpiration?: boolean
  previewData?: Record<string, unknown>
  disableForAdminLabel?: string
  disableForMemberLabel?: string
}

export const CommonEmail = ({
  type,
  platformId,
  withDisableForMember,
  withDisableForAdmin,
  withDaysPriorExpiration,
  previewData,
  disableForAdminLabel = 'Check this to disable the email sent to the administrator when a member registers for a free membership.',
  disableForMemberLabel = 'Check this to disable the email sent to a member when they register for a free membership.',
}: CommonEmailProps) => {
  const { template, updateTemplate, templateLoading, updateTemplateLoading, sendEmail, sendEmailLoading } =
    useEmailTemplate(type, platformId)
  const withConfig = withDisableForMember || withDisableForAdmin || withDaysPriorExpiration

  const initialValues: FormValues = {
    subject: template?.subject || '',
    content: template?.emailAsHtml || '',
    disableForAdmin: template?.config?.isDisabledForAdmins || false,
    disableForMember: template?.config?.isDisabledForUsers || false,
    daysPriorExpiration2: template?.config?.is2DaysPriorEnabled || false,
    daysPriorExpiration7: template?.config?.is7DaysPriorEnabled || false,
  }

  const handleSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
    try {
      await updateTemplate({
        id: template?.id,
        subject: values.subject,
        emailAsHtml: values.content,
        emailAsPlainText: stripHTML(values.content),
        ...(withConfig
          ? {
              config: {
                ...(withDisableForAdmin ? { isDisabledForAdmins: values.disableForAdmin } : {}),
                ...(withDisableForMember ? { isDisabledForUsers: values.disableForMember } : {}),
                ...(withDisableForMember
                  ? {
                      is2DaysPriorEnabled: values.daysPriorExpiration2,
                      is7DaysPriorEnabled: values.daysPriorExpiration7,
                    }
                  : {}),
              },
            }
          : {}),
      })
      form.reset(values)

      toast.success('Email template updated successfully')
    } catch (error) {
      toast.error('Failed to update email template')
    }
  }

  if (templateLoading) {
    return <Loader />
  }

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, dirty }) => (
        <form onSubmit={handleSubmit} css={commonClasses.form}>
          <InputField name="subject" label="Subject" validate={validateEmailSubject} css={commonClasses.input} />

          <EditorField
            type="default"
            name="content"
            label="Content"
            placeholder="Enter email content"
            validate={validateRequiredHtml}
          />

          {!!withDisableForAdmin && (
            <CheckboxWithTitle name="disableForAdmin" title="Disable for Admin" label={disableForAdminLabel} />
          )}

          {!!withDisableForMember && (
            <CheckboxWithTitle name="disableForMember" title="Disable for Member" label={disableForMemberLabel} />
          )}

          {!!withDaysPriorExpiration && (
            <div css={classes.wrapper}>
              <FormLabel css={classes.label}>Email Sent</FormLabel>
              <CheckboxField name="daysPriorExpiration2" label="2-days prior to expiration" />
              <CheckboxField name="daysPriorExpiration7" label="7-days prior to expiration" />
            </div>
          )}

          <FooterButtons
            onPreviewClick={sendEmail(previewData)}
            previewDisabled={updateTemplateLoading || sendEmailLoading}
            submitDisabled={updateTemplateLoading || sendEmailLoading || !dirty}
          />
        </form>
      )}
    />
  )
}
